import { useEffect, useMemo, useState } from "react";
import { Toaster, toast } from "@/components/ui/sonner";
import "@/App.css";
import { Header } from "@/components/landing/Header";
import { Hero } from "@/components/landing/Hero";
import { CompanySnapshot } from "@/components/landing/CompanySnapshot";
import { Milestones } from "@/components/landing/Milestones";
import { LineCard } from "@/components/landing/LineCard";
import { RevenueGrowth } from "@/components/landing/RevenueGrowth";
import { TrinityModel } from "@/components/landing/TrinityModel";
import { TechnicalCommercialCapabilities } from "@/components/landing/TechnicalCommercialCapabilities";
import { Solutions } from "@/components/landing/Solutions";
import { GlobalPresence } from "@/components/landing/GlobalPresence";
import { Team } from "@/components/landing/Team";
import { ProofAreas } from "@/components/landing/ProofAreas";
import { Partnership } from "@/components/landing/Partnership";
import { ContactForm } from "@/components/landing/ContactForm";
import { PhisonPartnership } from "@/components/landing/PhisonPartnership";
import { Footer } from "@/components/landing/Footer";
import { CONTENT_VERSION, getEditableSeedContent, getLocalizedContentFromEditable, normalizeEditableContent } from "@/data/trinityI18n";
import { fetchSiteContent } from "@/lib/contentApi";
import AdminEditor from "@/pages/AdminEditor";

const renderOrderedSection = (sectionKey, content) => {
  const sections = {
    hero: <Hero content={content} />,
    snapshot: <CompanySnapshot content={content.snapshot} brand={content.brand} images={content.snapshotImages} />,
    lineCard: <LineCard content={content.lineCard} logos={content.partnerLogos} images={content.lineCardImages} />,
    milestones: <Milestones content={content.milestones} brand={content.brand} images={content.milestoneImages} />,
    revenue: <RevenueGrowth content={content.revenue} />,
    trinityModel: <TrinityModel positioning={content.positioning} operatingModel={content.operatingModel} modelFlow={content.modelFlow} />,
    proof: <ProofAreas content={content.proof} images={content.proofImages} />,
    capabilities: <TechnicalCommercialCapabilities capabilities={content.capabilities} cameraExpertise={content.cameraExpertise} images={content.capabilityImages} />,
    solutions: <Solutions content={content.solutions} images={content.solutionImages} />,
    global: <GlobalPresence content={content.global} mapRegions={content.mapRegions} images={content.globalImages} />,
    phison: <PhisonPartnership content={content.phison} images={content.phisonImages} />,
    team: <Team content={content.team} images={content.teamImages} />,
    partnership: <Partnership content={content.partnership} brand={content.brand} images={content.partnershipImages} />,
  };
  return sections[sectionKey] || null;
};

const Home = () => {
  const [language, setLanguage] = useState("zh");
  const [editableContent, setEditableContent] = useState(getEditableSeedContent());
  const content = useMemo(() => getLocalizedContentFromEditable(language, editableContent), [language, editableContent]);

  useEffect(() => {
    fetchSiteContent()
      .then((contentData) => {
        if (contentData && Object.keys(contentData).length > 0 && contentData.version === CONTENT_VERSION) {
          setEditableContent(normalizeEditableContent(contentData));
        }
      })
      .catch(() => {});
  }, []);

  const sectionOrder = content.sectionOrder || ["hero", "snapshot", "lineCard", "trinityModel", "milestones", "phison", "revenue", "proof", "capabilities", "solutions", "global", "team", "partnership"];
  const hiddenSections = content.hiddenSections || [];

  return (
    <main data-testid="trinity-landing-page" dir={content.dir} lang={language}>
      <Toaster position="top-center" />
      <Header content={content} language={language} onLanguageChange={setLanguage} />
      {sectionOrder.filter((sectionKey) => !hiddenSections.includes(sectionKey)).map((sectionKey) => {
        const section = renderOrderedSection(sectionKey, content);
        return section ? <div key={sectionKey} data-testid={`section-wrapper-${sectionKey}`}>{section}</div> : null;
      })}
      {!hiddenSections.includes("contactForm") ? <ContactForm content={content.contactForm} /> : null}
      {!hiddenSections.includes("footer") ? <Footer content={content.footer} /> : null}
    </main>
  );
};

function App() {
  if (window.location.pathname === "/admin") {
    return <AdminEditor />;
  }
  return <Home />;
}

export default App;
